import React, { useState } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import ClassIcon from '../icons/ClassIcon';
import SchoolIcon from '../icons/SchoolIcon';
import PositionIcon from '../icons/PositionIcon';
import RulerVerticalIcon from '../icons/RulerVerticalIcon';
import ScaleIcon from '../icons/ScaleIcon';
import LinkIcon from '../icons/LinkIcon';
import JerseyIcon from '../icons/JerseyIcon';
import CheckmarkBadgeIcon from '../icons/CheckmarkBadgeIcon';
import BirthdayIcon from '../icons/BirthdayIcon';
import TransferPortalProfileIcon from '../icons/TransferPortalProfileIcon';
import RugbyIcon from '../icons/RugbyIcon';
import UserCircleIcon from '../icons/UserCircleIcon';
import RecruitingIcon from '../icons/RecruitingIcon';
import ContactCardIcon from '../icons/ContactCardIcon';
import PAIFilledIcon from '../icons/PAIFilledIcon';
import MPHFilledIcon from '../icons/MPHFilledIcon';
import PPIFilledIcon from '../icons/PPIFilledIcon';
import CheckCircleIcon from '../icons/CheckCircleIcon';
import WatchListIcon from '../icons/WatchListIcon';
import NFLIcon from '../icons/NFLIcon';
import SpeedIcon from '../icons/SpeedIcon';
import BannerIcon from '../icons/BannerIcon';
import TFVLogoIcon from '../icons/TFVLogoIcon';
import TFPillLogoIcon from '../icons/TFPillLogoIcon';
import ThreeX from '../icons/ThreeX';
import PffLinkIcon from '../icons/PffLinkIcon';
import OurladsIcon from '../icons/OurladsIcon';
import On3LogoIcon from '../icons/On3LogoIcon';
import AllConferenceIcon from '../icons/AllConferenceIcon';
import StarBadgeIcon from '../icons/StarBadgeIcon';
import TeamLogo from '../atoms/TeamLogo';
import ListItem from '../atoms/ListItem';
import NamedBox from '../atoms/NamedBox';
import Links from '../molecules/Links';
import ShowMoreDots from '../molecules/ShowMoreDots';
import WatchList from '../molecules/WatchList';
import { inchesToFeetAndInches, datetimeToDate } from '../services/converter';
import { isValidDOB, capitalizeFirstLetter, prepareLinks, prepareDivisionLabel } from '../services/player';
import {
  COLOR_BLUE,
  COLOR_ORANGE,
  COLOR_LIGHT_ORANGE,
  COLOR_BORDER,
  COLOR_DARK_BLUE,
  COLOR_DARK_GRAY,
  COLOR_DARK_GREEN,
  COLOR_MIDDLE_GRAY,
  COLOR_BLACK,
  COLOR_WHITE,
  COLOR_TEXT,
  COLOR_PURPLE
} from '../styles/colors';
import Player from '../types/Player';
import Position from '../types/Position';
import College from '../types/College';
import HighSchool from '../types/HighSchool';
import User from '../types/User';
import { LinkType } from '../types/Link';
import { Box, Button, Chip, Dialog, Grid, Typography } from '@material-ui/core';
import { PlayerNILValues } from '../types/PlayerNILValues';
import NilValuesTooltip from '../molecules/NilValuesTooltip';
import { getSingleServeUrl } from '../services/ssa-auth';
import RecruitingContent from '../molecules/RecruitingContent';
import NotAvailable from '../atoms/NotAvailable'
import ContactContent from '../molecules/ContactContent';
import { CollegeProductionRow } from '../types/CollegeProductionData';

const STARTER_THRESHOLD = 2; // Example threshold for determining if a player is a starter based on college production data
interface PlayerProfileCollegeOverviewProps {
  className?: string;
  player?: Player;
  selectedPosition?: Position;
  user: User | undefined;
  playerNilValues?: PlayerNILValues[];
  playerOn3Date?: string;
  collegeProductionData: CollegeProductionRow[] | undefined;
  
}

const useStyles = makeStyles((theme: Theme) => ({
  playerProfileCollegeOverview: {
  },
  headerClassName: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  headerName: {
    flexGrow: 1,
    paddingLeft: theme.spacing(2),
    fontWeight: 700,
  },
  actionWrap: {
    '& > button': {
      backgroundColor: 'inherit',
      borderRadius: 0,
      borderLeft: `1px solid ${COLOR_BORDER}`,
      boxShadow: 'none',
      color: COLOR_BLUE,
      padding: theme.spacing(2),

      '& > span': {
        fontSize: theme.typography.pxToRem(16),
        fontWeight: 700,
        textDecoration: 'underline',
        textTransform: 'capitalize',
      },

      '& .MuiSvgIcon-root': {
        color: COLOR_BLACK,
        fontSize: '28px',
        fontWeight: 'normal',
      },
    }
  },
  actionIcon: {
    display: 'flex',
    maxWidth: '28px',
    
    '& svg': {
      height: 'auto',
    }
  },
  column: {
    borderRight: `1px solid ${theme.palette.divider}`,
    
    "&:last-child": {
      borderRight: "none",
    },
  },
  listItem: {
    padding: theme.spacing(.5, 2.5),
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1.2,

    '& a': {
      color: COLOR_BLUE
    }
  },
  chip: {
    borderWidth: '2px',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 700,
    height: 26,
  },
  overviewChip: {
    color: COLOR_WHITE,
  },
  divisionChip: {
    borderColor: COLOR_ORANGE,
    backgroundColor: '#FFE2CF',
    color: COLOR_TEXT,
  },
  chipWithLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  chipLabel: {
    fontWeight: 700,
  },
  badges: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1, 0),
    alignItems: 'center',
    width: '100%',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    flexBasis: '33.3333%',
    gap: theme.spacing(.5),
    paddingRight: theme.spacing(1),
    fontWeight: 700,

    '&:last-child': {
      paddingRight: 0,
    },
  },
  badgeIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
      
    '& svg': {
      width: '100%',
      height: 'auto',
    },
  },
  badgeIconOrange: {
    color: COLOR_ORANGE
  },
  badgeIconGreen: {
    color: '#69BD28'
  },
  badgeIconPurple: {
    color: COLOR_PURPLE
  },
  badgeIconGray: {
    color: COLOR_DARK_GRAY
  },
  dividedContent: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(.5, 2),
  },
  dividedContentSection: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '30px',
    paddingRight: theme.spacing(2),
    borderRight: `1px solid ${COLOR_MIDDLE_GRAY}`,

    '&:last-child': {
      paddingRight: 0,
      borderRight: 0,
    },
  },
  tfvValue: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tfvIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
      
    '& svg': {
      width: '100%',
      height: 'auto',
    },
  },
  leftMargin: {
    marginLeft: '5px',
  },
  socialProfile: {
    marginLeft: theme.spacing(3),

    '&:first-of-type': {
      marginLeft: 0,
    },
  },
  teamLogo: {
    height: '32px',
    marginRight: theme.spacing(2),
  },
  link: {
    color: COLOR_BLUE,
    textDecoration: 'underline',
    fontWeight: 700,

    '&:hover': {
      color: COLOR_DARK_BLUE,
    },
  },
  collegeStatus: {
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
    lineHeight: 1,
  },
  active: {
    color: COLOR_DARK_GREEN,
  },
  former: {
    color: COLOR_DARK_GRAY,
  },
  more: {
    marginLeft: theme.spacing(2),
  },
  formerColleges: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    listStyle: 'none',
    padding: 0,
  },
  formerCollege: {},
  transferYear: {
    marginLeft: '5px'
  },
  positionText: {
    fontWeight: 600,
    marginRight: '8px',
  },
  positionCode: {
    marginRight: '5px'
  },
  depthChartContent: {
    alignItems: 'start',
    flexDirection: 'column',
  },
}), { name: PlayerProfileCollegeOverview.name });

export default function PlayerProfileCollegeOverview (props: PlayerProfileCollegeOverviewProps) {
  const {
    className,
    player,
    selectedPosition,
    user,
    playerNilValues=[],
    playerOn3Date='',
    collegeProductionData
  } = props;
  const classes = useStyles();
  const [ssaLoading, setSsaLoading] = useState<boolean>(false);
  const [isRecruitingDialogOpen, setIsRecruitingDialogOpen] = useState<boolean>(false);
  const [isContactPlayerDialogOpen, setIsContactPlayerDialogOpen] = useState<boolean>(false);

  const primarySchool = ((player && player.highSchools) || [])
      .find((highSchool: HighSchool) => highSchool && highSchool.isPrimary);
  const primaryCollege = (player?.playerColleges || [])
    .find((college:College) => !!college?.isPrimary);
  const isPrimaryCollegePrimaryPosition = primaryCollege?.positions?.find(position => position.code === selectedPosition)?.isPrimary || false;
  const formerColleges = (player?.playerColleges || [])
    .filter((college:College) => !!college?.isFormer)
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  const highSchool = (player?.highSchools || [])
    .find((highSchool:HighSchool) => highSchool && highSchool.isPrimary);
  const team = primaryCollege?.team;

  const rosterHeight = primaryCollege?.height || highSchool?.height || null;
  const rosterWeight = primaryCollege?.weight || highSchool?.weight || null;
  const dob = !!player?.dob && datetimeToDate(player.dob);
  const jerseyNumber = primaryCollege?.jerseyNumber;
  const transferStatus = player?.transferStatus;
  const links = prepareLinks(player);
  const socialLinks = links.filter(link => link.key === LinkType.instagram || link.key === LinkType.twitter);
  const allLinks = links.filter(link => link.key !== LinkType.instagram && link.key !== LinkType.twitter);
  const ssaLink = (links?.find(link => link.key === LinkType.ssa) || {})?.value;
  const depthChartData = player?.depthChart ? player?.depthChart.split('/').map(item => item.trim()).filter(Boolean): [];
  const on3PublicNILVal = player?.on3PublicNILValuation || null;
  const division  = primaryCollege?.team?.conference?.mainLevel || '';
  const starterYear = collegeProductionData?.map(row => row.gamesStarted).filter(gamesStarted => gamesStarted >= STARTER_THRESHOLD).length || 0;
  
  function onSsaLinkClick() {
    setSsaLoading(true); // Show spinner
    getSingleServeUrl(null, ssaLink).then((link:string) => {
      window.location.href = link;
    }).finally(() => setSsaLoading(false)); // Hide spinner
  }


  return (
    <NamedBox
      className={clsx(classes.playerProfileCollegeOverview, className)}
      name='College Overview'
      headerNameClassName={classes.headerName}
      headerClassName={classes.headerClassName}
      headerContent={
        <Box className={classes.actionWrap}>
          <Button variant="contained" endIcon={<RecruitingIcon className={classes.actionIcon}/>}
          onClick={() => setIsRecruitingDialogOpen(true)} >
            Recruiting
          </Button>
          <Button variant="contained" endIcon={<ContactCardIcon className={classes.actionIcon}/>}
          onClick={() => setIsContactPlayerDialogOpen(true)}
          >
            Contact Player
          </Button>
        </Box>
      }
    >
      <Grid container>
        {/* Left Column */}
        <Grid item xs={12} sm={6} md={4} className={classes.column}>
        {(player && team?.name) && (
            <ListItem
              icon={SchoolIcon}
              className={classes.listItem}
              title='College:'
            >
              <TeamLogo
                className={classes.teamLogo}
                team={team}
              />

              {user?.accessLevel?.accessCollegeTeamPage
                ? <Link
                    to={`/team/${team.slug}`}
                    className={classes.link}
                  >
                    <b>{team.name}</b>
                  </Link>
                : <b>{team.name}</b>
              }

              {(user?.accessLevel?.accessCollegeTeamPage && primaryCollege) && (
                <span
                  className={clsx(
                    classes.collegeStatus,
                    primaryCollege?.isFormer ? classes.former : classes.active,
                  )}
                >
                  {primaryCollege?.isFormer ? '(Former)' : '(Active)'}
                </span>
              )}

              {!!formerColleges.length && (
                <ShowMoreDots
                  className={classes.more}
                  tooltipContent={(
                    <ul className={classes.formerColleges}>
                      {formerColleges.map(college => (
                        <li
                          key={college.id}
                          className={classes.formerCollege}
                        >
                          {college?.team?.name}
                        </li>
                      ))}
                    </ul>
                  )}
                />
              )}
            </ListItem>
          )}

          {selectedPosition && (
            <ListItem
              icon={PositionIcon}
              className={classes.listItem}
              title='Position:'
            >
              <div className={classes.chipWithLabel}>
                <Chip label={selectedPosition} className={clsx(classes.chip, classes.overviewChip)} color="secondary" />
                { isPrimaryCollegePrimaryPosition && <div className={classes.chipLabel}>(primary)</div>}
              </div>
            </ListItem>
          )}

          {!!rosterHeight && (
            <ListItem
              icon={RulerVerticalIcon}
              className={classes.listItem}
              title='Height:'
            >
              <b>{inchesToFeetAndInches(rosterHeight)}</b>
            </ListItem>
          )}

          {!!rosterWeight && (
            <ListItem
              icon={ScaleIcon}
              className={classes.listItem}
              title='Weight:'
            >
              <b>{rosterWeight} lbs.</b>
            </ListItem>
          )}

          {!!jerseyNumber && (
            <ListItem
              icon={JerseyIcon}
              className={classes.listItem}
              title='Jersey:'
            >
              <Chip label={jerseyNumber} variant="outlined" className={classes.chip} />
            </ListItem>
          )}

          <ListItem
            icon={ClassIcon}
            className={classes.listItem}
            title='Recruiting Class:'
          >
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <b>{primaryCollege?.recruitingClass || highSchool?.graduatingClass || 0}</b>
              </div>

              <div className={classes.dividedContentSection}>
                <span>HS Class: <b>{highSchool?.graduatingClass || 0}</b></span>
              </div>
            </div>
          </ListItem>

          <ListItem
            icon={CheckmarkBadgeIcon}
            className={classes.listItem}
            title='Eligibility:'
          >
            {/* Need to update */}
            <b><NotAvailable /></b>
          </ListItem>

          {dob && isValidDOB(dob) && (
            <ListItem
              icon={BirthdayIcon}
              className={classes.listItem}
              title='DOB:'
            >
              <b>{dob}</b>
            </ListItem>
          )}
        </Grid>
        
      {/* middle Column */}
      <Grid item xs={12} sm={6} md={4} className={classes.column}>
        <ListItem
          icon={RugbyIcon}
          className={classes.listItem}
          title='Division:'
        >
          <Chip label={prepareDivisionLabel(division)} className={clsx(classes.chip, classes.divisionChip)} variant="outlined" />
        </ListItem>

        {/* {(team?.city || team?.state) && (
          <ListItem
            className={classes.listItem}
            title='College Location:'
          >
            <b>{team?.city || ''}{(team?.city && team?.state) ? `, ${team.state}` : (!team?.city && team?.state) ? team?.state : ''}</b>
          </ListItem>
        )} */}

        <ListItem
          icon={TransferPortalProfileIcon}
          className={classes.listItem}
          title='Portal:'
        >
          <b className={transferStatus == 'active' ? classes.active : ''}>{capitalizeFirstLetter(transferStatus || '') || "N/A"}</b>
          
          {player?.transferYear && (
            <b className={classes.transferYear}>{player?.transferYear}</b>
          )}
        </ListItem>

        {primaryCollege?.url && (
          <ListItem
            icon={UserCircleIcon}
            className={classes.listItem}
            title='Roster:'
          >
            <a
              className={classes.link}
              href={primaryCollege?.url}
              target='_blank'
            >
              Roster Bio
            </a>
          </ListItem>
        )}

        {!!links?.length && (links.find(link => link.key === LinkType.pff) || {}).value && (
          <ListItem
            className={classes.listItem}
            icon={PffLinkIcon}
            title='Link:'
          >
            <a
              className={classes.link}
              href={`${(links.find(link => link.key === LinkType.pff) || {}).value}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              PFF Ultimate Link
            </a>
          </ListItem>
        )}

        {!!depthChartData.length && (
          <ListItem
            icon={OurladsIcon}
            className={classes.listItem}
            title='Depth Chart:'
            contentClassName={classes.depthChartContent}
          >
            <Typography className={classes.positionText}>
              {depthChartData.map((data, index) => (
                <span key={index} className={classes.positionCode}>{data}</span>
              ))}
              {player?.ourladsUpdatedDate ? `Updated ${datetimeToDate(player?.ourladsUpdatedDate)}` : ""}
            </Typography>
          </ListItem>
        )}

        <ListItem
          icon={SpeedIcon}
          className={classes.listItem}
          title="Speed Player"
        >
          {
            player?.isSpeedRecruit ? <div className={classes.badge}>
              <CheckCircleIcon className={clsx(classes.badgeIcon, classes.badgeIconOrange)} />
              Speed
            </div> : <NotAvailable />
          }
        </ListItem>

        <ListItem
          icon={ThreeX}
          className={classes.listItem}
          title="TF Triple Verified:"
        >
          {(player?.pai?.length ?? 0) > 0 && (
            <div className={classes.badge}>
              <PAIFilledIcon className={classes.badgeIcon} />
              PAI
            </div>
          )}
          
          {(player?.ppi ?? 0) > 0 && (
            <div className={classes.badge}>
              <PPIFilledIcon className={classes.badgeIcon} />
              PPI
            </div>
          )}

          {(player?.bestCollegeMPH?.mph ?? 0) > 0 && (
            <div className={classes.badge}>
              <MPHFilledIcon className={classes.badgeIcon} />
              MPH
            </div>
          )}
        </ListItem>

        {/* profiles */}
        <ListItem
        icon={UserCircleIcon}
        className={classes.listItem}
        title="Profiles"
        >
          <Links links={allLinks} />         
        </ListItem>
      </Grid>
      
      {/* Right Column */}
      <Grid item xs={12} md={4} className={classes.column}>
        {/* {on3PublicNILVal &&  */}
          <ListItem
            icon={On3LogoIcon}
            className={classes.listItem}
            title='Total Player Value:'
          >
            <b>${on3PublicNILVal || 0}</b>
            <NilValuesTooltip playerNilValues={playerNilValues} />
            <b className={classes.leftMargin}>{playerOn3Date && datetimeToDate(playerOn3Date)}</b>
          </ListItem>
        {/* } */}

        <ListItem
          icon={TFPillLogoIcon}
          className={classes.listItem}
          title="TFV Value:"
        >
          <div className={classes.dividedContent}>
            <div className={classes.dividedContentSection}>
              <b>{player?.tfvs || <NotAvailable />}</b>
            </div>
            
            <div className={classes.dividedContentSection}>
              <div className={classes.tfvValue}>
                <TFVLogoIcon className={classes.tfvIcon} />
                {/* Need to update */}
                <span>Score: <b>4.0</b></span>
              </div>
            </div>
          </div>
        </ListItem>

        <ListItem
          icon={BannerIcon}
          className={classes.listItem}
          title="Starter"
        >
          <b>{starterYear ? `${starterYear}-year NCAA starter` : <NotAvailable />}</b>   
        </ListItem>

        {/* {!!player?.allConference?.length && ( */}
          <ListItem
            icon={AllConferenceIcon}
            className={classes.listItem}
            title='All-Conference:'
          >
            <b>{player?.allConference.join(', ')}</b>
          </ListItem>
        {/* )} */}

        {/* {!!player?.allAmerican?.length && ( */}
          <ListItem
            icon={StarBadgeIcon}
            className={classes.listItem}
            title='All-American:'
          >
            <b>{player?.allAmerican.join(', ')}</b>
          </ListItem>
        {/* )} */}

          <ListItem
            icon={WatchListIcon}
            className={classes.listItem}
            title='Watch List:'
          >
            {!!player?.watchList?.length ?
              <WatchList list={player.watchList} />
            : <NotAvailable />}
          </ListItem>
        {/* )} */}

        <ListItem
          icon={NFLIcon}
          className={classes.listItem}
          title='Draft Projection:'
        >
          {
            player?.nflDraftRound || player?.nflDraftPick
            ? <b> {player?.nflDraftRound ? `Round ${player?.nflDraftRound}` : ''}  {player?.nflDraftPick ? `Pick ${player?.nflDraftPick}` : ''}</b>
            : <NotAvailable />
          }
          
        </ListItem>

        <ListItem
          icon={LinkIcon}
          className={classes.listItem}
          title="Social Profiles"
        >
          <Links links={socialLinks} />         
        </ListItem>
      </Grid>
    </Grid> 

      <Dialog 
        open={isRecruitingDialogOpen}
        title='Recruiting'
        onClose={() => setIsRecruitingDialogOpen(false)}
      >
        {/* {recruitingContent()} */}
        <RecruitingContent primarySchool={primarySchool} player={player} links={links} />
      </Dialog>

      <Dialog 
        open={isContactPlayerDialogOpen}
        title='Contact Player'
        onClose={() => setIsContactPlayerDialogOpen(false)}
      >
        <ContactContent socialLinks={socialLinks} primarySchool={primarySchool} player={player} links={links}/>
      </Dialog>

    </NamedBox>
  );
}
